import Link from 'next/link';
import {
  useProductByHandle,
  useSettings,
  useLocalizedVariant,
  useCountry,
  useCartAddItem,
} from '@backpackjs/storefront';
import { IconRemove, IconMinus, IconPlus } from '@/snippets/index';
import styles from './Cart.module.css';
import { getSizedImageUrl } from '../../utilities/Images';
import { formatMoney } from '../../utilities/Helper';
import Cookies from 'js-cookie';
import { getMetafieldMap } from '@/utils/Metafields';

function CartItem({ item, cartUpdateItem, cartRemoveItem, isUs }) {
  const siteSettings = useSettings();
  const { country } = useCountry();
  const { cartAddItem } = useCartAddItem();
  const isFreeItem = item.attributes.some((attr) =>
    ['_gwp'].includes(attr.key)
  );

  const { product } = useProductByHandle({
    handle: item?.variant?.product?.handle,
  });
  const sellingPlans = (product?.sellingPlanGroups || []).filter(
    (group) => group?.appName === '294517'
  );
  const hasSellingPlan = item?.sellingPlanAllocation;
  const planAmount =
    item?.sellingPlanAllocation?.priceAdjustments?.[0]?.price?.amount;
  const planText =
    item?.sellingPlanAllocation?.sellingPlan?.name?.replace(/Delivery/i, 'Ships');
  const planPrice = planAmount ? parseFloat(planAmount) : null;
  const planOptions = sellingPlans?.map((plan) => ({
    label: plan?.sellingPlans?.[0]?.name?.replace('Delivery', 'Deliver'),
    value: plan?.sellingPlans?.[0]?.id,
  }));
  const sellingPlanOptions = planOptions?.length
    ? [{ label: 'One-time Purchase', value: 'one-time' }, ...planOptions]
    : [];

  const { localized: localizedVariant } = useLocalizedVariant({
    variant: item.variant,
  });
  const metafieldMap = getMetafieldMap(product);
  const comparePriceText = metafieldMap?.custom_compare_at_price || '';
  const hasDiscount = Number.parseFloat(item.estimatedCost.subtotalAmount.amount) !== Number.parseFloat(item.estimatedCost.totalAmount.amount);
  const selectedVariant = { ...item.variant, ...localizedVariant };
  const variantAmount = item.estimatedCost.totalAmount.amount / item.quantity;
  let compareAmount = hasDiscount ? Number.parseFloat(item.estimatedCost.subtotalAmount.amount) / item.quantity : Number.parseFloat(selectedVariant?.compareAtPriceV2?.amount);
  if (hasSellingPlan) compareAmount = Number.parseFloat(selectedVariant?.compareAtPriceV2?.amount) || Number.parseFloat(selectedVariant?.priceV2?.amount);
  if (compareAmount < Number.parseFloat(selectedVariant?.compareAtPriceV2?.amount)) compareAmount = Number.parseFloat(selectedVariant.compareAtPriceV2.amount);

  const currencySymbol = country?.currency?.symbol;
  const comparePrice = compareAmount
    ? compareAmount
    : null;
  const variantPrice = variantAmount;

  const handlePlanChange = async (e) => {
    if (e.target.value === 'one-time') {
      await cartRemoveItem({ lineId: item.id });

      await cartAddItem({
        merchandiseId: item.variant.id,
        quantity: item.quantity,
        attributes: [
          {
            key: '_sc',
            value: (typeof window.ShipScoutLineItemProperty !== 'undefined' ? window.ShipScoutLineItemProperty : 'null'),
          }
        ]
      });
    } else {
      await cartUpdateItem({
        lineId: item.id,
        sellingPlanId: btoa(e.target.value),
      });
    }
  };

  const upgradeToPlan = async () => {
    const firstPlan = sellingPlans?.[0]?.sellingPlans?.[0];

    if (firstPlan) {
      await cartUpdateItem({
        lineId: item.id,
        sellingPlanId: btoa(firstPlan?.id),
      });
    }
  };

  const calcDiscountValue = () => {
    let discountValue = null;
    let currentAmount = variantAmount;

    if(planPrice) {
      currentAmount = parseFloat(item.estimatedCost.totalAmount.amount) < parseFloat(planPrice) ? parseFloat(item.estimatedCost.totalAmount.amount) : parseFloat(planPrice);
    }

    if(compareAmount) {
      discountValue = (compareAmount - currentAmount) / compareAmount * 100;
      discountValue = Math.round(discountValue).toFixed(0);
    }

    return discountValue;
  }

  const discountValue = calcDiscountValue();

  const discountHighlight =
    siteSettings?.discountHighlight?.enableDiscountHighlight &&
    discountValue !== 0 &&
    discountValue !== null;

  return (
    <div
      className={`${
        isFreeItem
          ? 'free-item-line-item-container items-start'
          : 'items-center'
      } cart-items-container border-b border-grey-4 px-4 py-6 lg:px-0 lg:py-7`}
    >
      <div
        className={`flex justify-between ${
          isFreeItem ? 'items-start' : 'items-center'
        } px-4 py-6 lg:px-0 lg:py-7`}
      >
        <div className="w-[90px] shrink-0 self-start">
          <Link
            href={`${
              isFreeItem ? '' : `/products/${item?.variant?.product?.handle}`
            }`}
          >
            <a
              className={`responsive-image pb-full bg-grey-9 no-underline ${
                isFreeItem ? 'pointer-events-none' : ''
              }`}
            >
              <img
                src={getSizedImageUrl(item?.variant?.image?.src, '100x115@2x')}
                alt={item?.variant?.product?.title}
                width="100"
                height="115"
              />
            </a>
          </Link>
        </div>
        <div className="relative flex-grow px-4 lg:pl-6">
          <Link
            href={`${
              isFreeItem ? '' : `/products/${item?.variant?.product?.handle}`
            }`}
          >
            <a
              className={`my-0 pr-6 font-hafferBold text-xs no-underline lg:pr-0 ${
                isFreeItem ? 'pointer-events-none' : ''
              }`}
            >
              {item?.variant?.product?.title}
            </a>
          </Link>
          {item?.variant?.title !== 'Default Title' ? (
            <p className="mb-0 mt-0 text-xs lg:mt-2">{item?.variant?.title}</p>
          ) : null}
          <div className="mr-[15px] max-w-[320px] md:mr-[60px]">
            {planText ? (
              <p className="mb-0 mt-1 text-xs lowercase">
                {`${planText}`}
                {discountHighlight && discountValue > 0 && (
                  <span className="lineItem__discount-percentage ml-1 inline-block rounded-sm border border-solid border-primary-2 px-1 py-[1px] font-hafferBold text-sub-xx-small text-primary-2">
                    {discountValue}% off
                  </span>
                )}
              </p>
            ) : (
              <p className="mb-0 mt-1 text-xs lowercase">
                One-time Purchase
                {discountHighlight && discountValue > 0 && (
                  <span className="lineItem__discount-percentage ml-1 inline-block rounded-sm border border-solid border-primary-2 px-1 py-[1px] font-hafferBold text-sub-xx-small text-primary-2">
                    {discountValue}% off
                  </span>
                )}
              </p>
            )}
            {hasSellingPlan && (
              <div className="select-wrap-3 relative flex flex-col">
                <select
                  className="mt-[16px] h-[40px] w-auto cursor-pointer border border-grey-5 px-3 py-2 pr-[45px] text-[12px] text-black placeholder-black outline-0 transition-all placeholder-shown:border-grey-5 hover:border-black hover:placeholder-black focus:placeholder-black"
                  name={`selling-plan-select-${item?.id}`}
                  id={`selling-plan-select-${item?.id}`}
                  value={item?.sellingPlanAllocation?.sellingPlan?.id}
                  onChange={handlePlanChange}
                >
                  {sellingPlanOptions?.map((option, optionIndex) => (
                    <option
                      key={`option-${item?.id}-${optionIndex}`}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!hasSellingPlan && sellingPlans.length > 0 && isUs && (
              <button
                type="button"
                className="cart-upgrade-sub mt-5 hidden cursor-pointer whitespace-nowrap border-b-2 font-hafferBold text-sm md:inline-block"
                onClick={upgradeToPlan}
              >
                {sellingPlans?.[0]?.priceAdjustments?.[0]?.adjustmentValue
                  ? `update to subscription & save ${sellingPlans?.[0]?.priceAdjustments?.[0]?.adjustmentValue?.adjustmentPercentage}%`
                  : `update to subscription`}
              </button>
            )}
          </div>

          <div className="lg:hidden">
            <div className="mt-6 flex items-center justify-between">
              <ItemQuantity
                isFreeItem={isFreeItem}
                cartUpdateItem={cartUpdateItem}
                cartRemoveItem={cartRemoveItem}
                item={item}
              />
              <ItemTotal
                className="text-right text-sm"
                isFreeItem={isFreeItem}
                comparePrice={comparePrice}
                planPrice={planPrice}
                variantPrice={variantPrice}
                currencySymbol={currencySymbol}
                comparePriceText={comparePriceText}
              />
            </div>
            <button
              type="button"
              className={`${styles.lineItemDelete} free? absolute right-0 top-0 inline-block cursor-pointer`}
              onClick={() => {
                cartRemoveItem({ lineId: item.id });
                if (
                  item.attributes.find((attribute) => attribute.key === '_gwp')
                ) {
                  const optedOutCampaigns =
                    Cookies.get('OptedOutCampaigns')?.split(',') || [];
                  const campaign = item.attributes.find(
                    (attribute) => attribute.key === '_campaign'
                  ).value;
                  Cookies.set('OptedOutCampaigns', [
                    ...optedOutCampaigns,
                    campaign,
                  ]);
                }
              }}
            >
              <IconRemove />
            </button>
          </div>
        </div>

        <div className="max-lg:hidden lg:flex lg:items-center">
          <ItemQuantity
            className="w-28"
            isFreeItem={isFreeItem}
            cartUpdateItem={cartUpdateItem}
            cartRemoveItem={cartRemoveItem}
            item={item}
          />
          <ItemTotal
            className="w-28 shrink-0 text-center text-sm"
            isFreeItem={isFreeItem}
            comparePrice={comparePrice}
            planPrice={planPrice}
            variantPrice={variantPrice}
            currencySymbol={currencySymbol}
            comparePriceText={comparePriceText}
          />

          <div className="w-20 shrink-0 text-center">
            <button
              type="button"
              className={`${styles.lineItemDelete} inline-block cursor-pointer`}
              onClick={() => {
                cartRemoveItem({ lineId: item.id });
                if (
                  item.attributes.find((attribute) => attribute.key === '_gwp')
                ) {
                  const optedOutCampaigns =
                    Cookies.get('OptedOutCampaigns')?.split(',') || [];
                  const campaign = item.attributes.find(
                    (attribute) => attribute.key === '_campaign'
                  ).value;
                  Cookies.set('OptedOutCampaigns', [
                    ...optedOutCampaigns,
                    campaign,
                  ]);
                }
              }}
            >
              <IconRemove />
            </button>
          </div>
        </div>
      </div>
      {!hasSellingPlan && sellingPlans.length > 0 && isUs && (
        <div className="w-full px-4 text-right">
          <button
            type="button"
            className="cart-upgrade-sub mx-auto mt-0 inline-block cursor-pointer border-b-2 font-hafferBold text-sm md:hidden md:whitespace-nowrap"
            onClick={upgradeToPlan}
          >
            {`update to subscription & save ${sellingPlans?.[0]?.sellingPlans?.[0]?.priceAdjustments?.[0]?.adjustmentValue?.adjustmentPercentage}%`}
          </button>
        </div>
      )}
    </div>
  );
}

function ItemQuantity({
  className = '',
  isFreeItem,
  cartUpdateItem,
  cartRemoveItem,
  item,
}) {
  if (isFreeItem) return <span />;

  return (
    <div
      className={`${className} rounded-xs flex justify-between border border-black`}
    >
      <button
        type="button"
        className="flex cursor-pointer items-center pl-3"
        onClick={() => {
          if (item.quantity === 1) {
            cartRemoveItem({ lineId: item.id });
            return;
          }
          cartUpdateItem({
            lineId: item.id,
            quantity: item.quantity - 1,
          });
        }}
      >
        <IconMinus />
      </button>
      <span className="flex-grow py-2 px-4 text-center">{item.quantity}</span>
      <button
        type="button"
        className={`flex cursor-pointer items-center pr-3 ${
          item.quantity >= 5 ? 'pointer-events-none opacity-50' : ''
        }`}
        onClick={() => {
          if (item.quantity >= 5) return;
          cartUpdateItem({
            lineId: item.id,
            quantity: item.quantity + 1,
          });
        }}
      >
        <IconPlus />
      </button>
    </div>
  );
}

function ItemTotal({
  className = '',
  isFreeItem,
  comparePrice,
  planPrice,
  variantPrice,
  currencySymbol,
  comparePriceText = '',
}) {
  if (isFreeItem) {
    return (
      <div className={`${styles.lineItemPricing} ${className}`}>
        <span className={styles.lineItemPrice}>FREE</span>
      </div>
    );
  }

  return (
    <div className={`${styles.lineItemPricing} ${className}`}>
      {comparePrice > variantPrice && comparePriceText === '' ? (
        <span
          className={styles.lineItemCompareAtPrice}
        >
          {formatMoney(comparePrice.toFixed(2)).replace('$', currencySymbol)}
        </span>
      ) : null}
      {comparePriceText !== '' && (
        <span className={`${styles.lineItemCompareAtPrice} after:hidden inline-block`}>{comparePriceText}</span>
      )}
      {planPrice ? (
        <span className={styles.lineItemPrice}>{formatMoney(planPrice?.toFixed(2)).replace('$', currencySymbol)}</span>
      ) : (
        <span className={styles.lineItemPrice}>{formatMoney(variantPrice.toFixed(2)).replace('$', currencySymbol)}</span>
      )}
    </div>
  );
}

export default CartItem;